import { determineIfMultiSelectable } from './addSelectFunctionalityToDrawable'
import { PAPER_ELEMENT_TYPE } from '../../../../shared/constants/drawable-types'
import store from '../../../../stores'
import {
    bulkUpdateOpeningLocationCoordinatesAction,
    BulkUpdateOpeningLocationCoordinatesPayload,
} from '../../../sagas/2D/bulkUpdateOpeningLocationCoordinates'
import { saveEditsToHighlight } from '../../../sagas/2D/editHighlights'
import { setActiveHighlightId, setActiveHighlightOpeningId, updateSelectedPaperId } from '../../../slices/2D'
import { setHighlightPopupColor } from '../../../slices/common'
import { IMUP2DCoordinatesToUpdate, ROUNDED_CORNER_SIZE_IN_PX } from '../../../types'
import managers from '../../managers'
import { PathTool, Workspace } from '../../toolBoxes/2D'

function save2DHighlight(): (coordinatesToUpdate: IMUP2DCoordinatesToUpdate, projectId: number) => void {
    return function saveHighlight(this: paper.Path, coordinatesToUpdate) {
        store.dispatch(
            saveEditsToHighlight({
                coordsMeasurementsAndCutouts: coordinatesToUpdate,
                highlightDrawableId: this.data.drawable_id,
                highlightLocationId: this.data.opening_location_id,
            })
        )

        // OriginalCoordinates are needed for path rounding and derounding
        // so updated on each save
        this.data.originalCoordinates = coordinatesToUpdate.coordinates
    }
}

function multiSelectSave2DHighlight(): (payload: BulkUpdateOpeningLocationCoordinatesPayload) => void {
    return function saveHighlight(coordinatesToUpdate) {
        store.dispatch(bulkUpdateOpeningLocationCoordinatesAction(coordinatesToUpdate))

        // Update originalCoordinates on each highlight item with the new coordinates
        // This ensures deroundPath uses the correct coordinates when the item is selected
        const manager = managers.get2DManager()

        if (manager) {
            const workspace: Workspace = manager.getTool(Workspace.NAME)

            coordinatesToUpdate.forEach((item) => {
                const paperItems = workspace.getItemsWithOpeningLocationId(item.opening_location_id)

                paperItems?.forEach((paperItem) => {
                    if (paperItem?.data.originalCoordinates) {
                        paperItem.data.originalCoordinates = item.coordinates
                    }
                })
            })
        }
    }
}

function select2DHighlight(): () => void {
    return function selectHighlight(this: paper.Path) {
        store.dispatch(setActiveHighlightId(this.id))

        store.dispatch(setActiveHighlightOpeningId(this.data.drawable_id ?? null))

        store.dispatch(updateSelectedPaperId(this.id))

        store.dispatch(setHighlightPopupColor(this?.fillColor?.toCSS(true) || null))

        const manager = managers.get2DManager()

        if (manager) {
            const pathTool: PathTool = manager.getTool(PathTool.NAME)

            pathTool.deroundPath(this)
        }
    }
}

function deselect2DHighlight(): () => void {
    return function deselectHighlight(this: paper.Path) {
        store.dispatch(setActiveHighlightId(null))

        store.dispatch(setActiveHighlightOpeningId(null))

        store.dispatch(updateSelectedPaperId(null))

        store.dispatch(setHighlightPopupColor(null))

        const manager = managers.get2DManager()

        if (manager) {
            const pathTool: PathTool = manager.getTool(PathTool.NAME)

            pathTool.roundPath(this, ROUNDED_CORNER_SIZE_IN_PX)
        }
    }
}

function multiDeselect2DHighlight(): () => void {
    return function deselectHighlight(this: paper.Path) {
        store.dispatch(setHighlightPopupColor(null))
    }
}

export default function addSelectFunctionalityToHighlight(path: paper.PathItem): void {
    path.data.type = PAPER_ELEMENT_TYPE.HIGHLIGHT
    path.data.isMultiSelectable = determineIfMultiSelectable(path)
    path.data.onSave = save2DHighlight().bind(path)
    path.data.onMultiSelectSave = multiSelectSave2DHighlight()
    path.data.onSelect = select2DHighlight().bind(path)
    path.data.onDeselect = deselect2DHighlight().bind(path)
    path.data.onMultiDeselect = multiDeselect2DHighlight().bind(path)
}
